import { z } from "zod";

import {
  OrgSchema,
  ProgramQuestionSchema,
  ProgramQuestionTypeSchema,
  ProgramRevisionStatus,
  ProgramRevisionStatusSchema,
  ProgramSchema,
  ProgramStatus,
  ProgramStatusSchema,
  ProgramType,
  ProgramTypeSchema,
  ReviewAction,
  ReviewActionSchema
} from "../models";

// Re-export enums for convenience
export {
  ProgramRevisionStatus,
  ProgramRevisionStatusSchema,
  ProgramStatus,
  ProgramType,
  ReviewAction,
  ReviewActionSchema
};

// ---------------------------------------------------------------------------
// DTO for a single program question (strips timestamps)
// ---------------------------------------------------------------------------

export const ProgramQuestionDtoSchema = ProgramQuestionSchema.omit({
  createdAt: true,
  updatedAt: true
});
export type ProgramQuestionDto = z.infer<typeof ProgramQuestionDtoSchema>;

// ---------------------------------------------------------------------------
// Detailed program view — flat shape merging Program metadata with active
// revision content fields. Kept flat for backwards compatibility.
// ---------------------------------------------------------------------------

export const ProgramWithDetailSchema = ProgramSchema.extend({
  org: OrgSchema.pick({ id: true, name: true, slug: true, avatar: true }),
  questions: z.array(ProgramQuestionDtoSchema),
  // Active revision content fields (null when no active revision yet)
  title: z.string(),
  description: z.string(),
  banner: z.string().nullable().optional(),
  type: ProgramTypeSchema,
  country: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  quota: z.number(),
  requirements: z.string().nullable().optional(),
  benefits: z.string().nullable().optional(),
  costVisible: z.boolean(),
  depositAmount: z.number(),
  deadline: z.coerce.date().nullable().optional(),
  // Revision lifecycle fields for org/admin UI
  pendingRevisionId: z.string().nullable().optional(),
  pendingRevisionStatus: ProgramRevisionStatusSchema.nullable().optional(),
  reviewNote: z.string().nullable().optional()
});
export type ProgramWithDetail = z.infer<typeof ProgramWithDetailSchema>;

// ---------------------------------------------------------------------------
// Create program DTO (creates the Program record + first DRAFT revision)
// ---------------------------------------------------------------------------

export const CreateProgramSchema = z.object({
  title: z.string().min(1).max(256),
  description: z.string().min(1),
  banner: z.string().url().nullable().optional(),
  type: ProgramTypeSchema,
  country: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  quota: z.number().int().min(1),
  requirements: z.string().nullable().optional(),
  benefits: z.string().nullable().optional(),
  costVisible: z.boolean().default(false),
  depositAmount: z.number().min(0).default(0),
  deadline: z.coerce.date().nullable().optional(),
  orgId: z.string()
});
export type CreateProgram = z.infer<typeof CreateProgramSchema>;

// ---------------------------------------------------------------------------
// Update program DTO — patches the current DRAFT revision
// ---------------------------------------------------------------------------

export const UpdateProgramSchema = CreateProgramSchema.omit({
  orgId: true
}).partial();
export type UpdateProgram = z.infer<typeof UpdateProgramSchema>;

// ---------------------------------------------------------------------------
// Update status DTO — kept for backwards compatibility during migration.
// The PATCH /programs/:id/status endpoint is removed; use the specific
// state-machine endpoints (submit, publish, pause, resume, close) instead.
// ---------------------------------------------------------------------------

export const UpdateProgramStatusSchema = z.object({
  status: ProgramStatusSchema
});
export type UpdateProgramStatus = z.infer<typeof UpdateProgramStatusSchema>;

// ---------------------------------------------------------------------------
// Create a new edit revision from the active published revision
// ---------------------------------------------------------------------------

export const CreateEditRevisionSchema = CreateProgramSchema.omit({
  orgId: true
}).partial();
export type CreateEditRevisionDto = z.infer<typeof CreateEditRevisionSchema>;

// ---------------------------------------------------------------------------
// Submit revision for admin review
// ---------------------------------------------------------------------------

export const SubmitForReviewSchema = z.object({
  submitNote: z.string().optional()
});
export type SubmitForReviewDto = z.infer<typeof SubmitForReviewSchema>;

// ---------------------------------------------------------------------------
// Admin review DTOs
// ---------------------------------------------------------------------------

export const AdminApproveRevisionSchema = z.object({
  note: z.string().optional()
});
export type AdminApproveRevisionDto = z.infer<typeof AdminApproveRevisionSchema>;

export const AdminRejectRevisionSchema = z.object({
  note: z.string().min(1, "Rejection note is required")
});
export type AdminRejectRevisionDto = z.infer<typeof AdminRejectRevisionSchema>;

// ---------------------------------------------------------------------------
// Query programs request
// ---------------------------------------------------------------------------

export const QueryProgramsReqSchema = z.object({
  page: z.coerce.number().min(0).default(0),
  limit: z.coerce.number().min(1).default(10),
  type: ProgramTypeSchema.optional(),
  status: ProgramStatusSchema.optional(),
  orgId: z.string().optional(),
  featured: z.coerce.boolean().optional(),
  search: z.string().optional()
});
export type QueryProgramsReq = z.infer<typeof QueryProgramsReqSchema>;

// ---------------------------------------------------------------------------
// Upsert program questions DTO (mirrors UpsertEventQuestions)
// ---------------------------------------------------------------------------

export const UpsertProgramQuestionsSchema = z.object({
  questions: z.array(
    z.object({
      id: z.string().optional(),
      type: ProgramQuestionTypeSchema,
      label: z.string().min(1).max(256),
      options: z.array(z.string()).default([]),
      required: z.boolean().default(true),
      order: z.number().int().min(0)
    })
  )
});
export type UpsertProgramQuestions = z.infer<typeof UpsertProgramQuestionsSchema>;
